import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles, PenSquare, ClipboardList, BookOpenText, RefreshCw, Link as LinkIcon } from "lucide-react";
import { analyzeThesisStrength } from "@/lib/thesisToolkit";

interface ThesisStatementPanelProps {
  currentDraft?: string;
  initialThesis?: string;
  onCopyThesis?: (thesis: string) => void;
  onInsertThesis?: (thesis: string) => void;
  onSyncThesis?: () => void;
  onThesisChange?: (value: string) => void;
  syncState?: { status: "idle" | "saving" | "success" | "error"; message?: string };
  onRequestRefine?: () => void;
  refining?: boolean;
  refinementResult?: {
    improvedText: string;
    rationale: string;
    talkingPoints: string[];
    hook?: string;
  } | null;
}

const THESIS_FRAMEWORKS = [
  {
    id: "claim",
    label: "Claim + Because",
    prompt: "What is your claim? Why is it true?",
    template: "[Claim] because [reason 1], [reason 2], and [reason 3].",
  },
  {
    id: "contrast",
    label: "Contrast",
    prompt: "What assumption are you challenging?",
    template: "Although [common belief], this paper argues [your stance] because [key evidence].",
  },
  {
    id: "causeEffect",
    label: "Cause & Effect",
    prompt: "What cause leads to what effect?",
    template: "[Cause] leads to [effect], which results in [impact].",
  },
];

const ThesisStatementPanel: React.FC<ThesisStatementPanelProps> = ({
  currentDraft,
  initialThesis,
  onCopyThesis,
  onInsertThesis,
  onSyncThesis,
  onThesisChange,
  syncState,
  onRequestRefine,
  refining,
  refinementResult,
}) => {
  const [topic, setTopic] = useState("");
  const [stance, setStance] = useState("");
  const [evidence, setEvidence] = useState("");
  const [refinedThesis, setRefinedThesis] = useState("");
  const [selectedFramework, setSelectedFramework] = useState("claim");
  const [notes, setNotes] = useState("");

  const { readabilityLabel, clarityNotes, structureScore } = useMemo(() => analyzeThesisStrength(refinedThesis), [refinedThesis]);

  const updateThesis = useCallback(
    (value: string) => {
      setRefinedThesis(value);
      onThesisChange?.(value);
    },
    [onThesisChange]
  );

  useEffect(() => {
    if (!refinedThesis && topic && stance) {
      updateThesis(`${stance} because ${topic}.`);
    }
  }, [stance, topic, refinedThesis, updateThesis]);

  useEffect(() => {
    if (typeof initialThesis === "string" && initialThesis !== refinedThesis) {
      setRefinedThesis(initialThesis);
    }
  }, [initialThesis, refinedThesis]);

  const handleGenerate = () => {
    const framework = THESIS_FRAMEWORKS.find((f) => f.id === selectedFramework);
    if (!framework) return;
    const template = framework.template
      .replace("[Claim]", stance || "My claim")
      .replace("[reason 1]", evidence || "reason one")
      .replace("[reason 2]", "additional support")
      .replace("[reason 3]", "final support")
      .replace("[common belief]", topic || "the common belief")
      .replace("[your stance]", stance || "my argument")
      .replace("[key evidence]", evidence || "key evidence")
      .replace("[Cause]", topic || "this cause")
      .replace("[effect]", stance || "this effect")
      .replace("[impact]", evidence || "a broader impact");
    updateThesis(template);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <BookOpenText className="w-5 h-5 text-amber-300" />
        <div>
          <p className="text-white font-semibold text-base">Thesis Statement Lab</p>
          <p className="text-xs text-white/60">Craft a defensible argument anchored in evidence.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <label className="text-xs text-white/60">Topic / Problem Space</label>
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g., community policing impacts" />
        </div>
        <div className="space-y-2">
          <label className="text-xs text-white/60">Stand / Claim</label>
          <Input value={stance} onChange={(e) => setStance(e.target.value)} placeholder="e.g., improves long-term safety" />
        </div>
        <div className="space-y-2">
          <label className="text-xs text-white/60">Key Evidence or Rationale</label>
          <Input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="e.g., reduces recidivism 22%" />
        </div>
        <div className="space-y-2">
          <label className="text-xs text-white/60">Framework</label>
          <select
            value={selectedFramework}
            onChange={(e) => setSelectedFramework(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
          >
            {THESIS_FRAMEWORKS.map((framework) => (
              <option key={framework.id} value={framework.id}>
                {framework.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
        <div className="flex items-center justify-between text-xs text-white/60">
          <span>Draft thesis</span>
          <button onClick={handleGenerate} className="inline-flex items-center gap-1 text-emerald-300">
            <Sparkles className="w-3 h-3" /> Suggest wording
          </button>
        </div>
        <Textarea
          value={refinedThesis}
          onChange={(e) => updateThesis(e.target.value)}
          className="min-h-[120px] bg-transparent text-sm"
          placeholder="Type or generate your thesis..."
        />
        {refinementResult && (
          <div className="mt-2 text-xs text-white/70 space-y-1">
            {refinementResult.hook && <p className="text-emerald-300">Hook: {refinementResult.hook}</p>}
            <p>{refinementResult.rationale}</p>
            {refinementResult.talkingPoints.length > 0 && (
              <ul className="list-disc list-inside text-white/60">
                {refinementResult.talkingPoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {syncState && syncState.status !== "idle" && (
        <div
          className={`text-[11px] px-3 py-2 rounded-lg border ${
            syncState.status === "error"
              ? "border-red-500/40 text-red-300"
              : syncState.status === "success"
              ? "border-emerald-500/30 text-emerald-200"
              : "border-white/15 text-white/60"
          }`}
        >
          {syncState.message || (syncState.status === "saving" ? "Syncing thesis..." : null)}
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
        <div className="text-xs text-white/60 uppercase tracking-[0.3em]">Readiness scan</div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 rounded-full border border-white/20 text-white/70">{readabilityLabel}</span>
          <span className="px-2 py-1 rounded-full border border-white/20 text-white/70">Structure {structureScore}/5</span>
        </div>
        <p className="text-xs text-white/70">{clarityNotes}</p>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
        <div className="text-xs text-white/60 uppercase tracking-[0.3em]">Working notes</div>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-[80px] bg-transparent text-sm" />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() => onInsertThesis?.(refinedThesis)}
          className="inline-flex items-center gap-2"
          disabled={!refinedThesis}
        >
          <PenSquare className="w-4 h-4" /> Insert into editor
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onCopyThesis?.(refinedThesis)}
          className="inline-flex items-center gap-2"
          disabled={!refinedThesis}
        >
          <ClipboardList className="w-4 h-4" /> Copy thesis
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onSyncThesis?.()}
          className="inline-flex items-center gap-2"
          disabled={!refinedThesis || syncState?.status === "saving"}
        >
          <LinkIcon className="w-4 h-4" /> Sync to organizer
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onRequestRefine?.()}
          className="inline-flex items-center gap-2"
          disabled={!refinedThesis || refining}
        >
          <RefreshCw className={`w-4 h-4 ${refining ? "animate-spin" : ""}`} />
          Refine with AI
        </Button>
      </div>
    </div>
  );
};

export default ThesisStatementPanel;
